import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex w-full align-items-baseline justify-content-end text-sm mb-s2" }
const _hoisted_2 = { class: "flex w-full align-items-center justify-content-between mb-s1" }
const _hoisted_3 = {
  key: 1,
  class: "flex align-items-center mt-s2"
}
const _hoisted_4 = {
  for: "stakeToFarm",
  class: "text-sm ml-s1"
}

import BigNumber from 'bignumber.js';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { USD_TOKEN } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/common/constants/USD_TOKEN';
import { USD_DECIMAL_PRECISION } from '@/constants/INTERFACE_SETTINGS';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { EasyModeAddLiquidityForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-form';
import { Token } from '@/sdk/entities/token';
import { max, min } from '@/utils/bigNumber';
import TokenDropdown from '@/components/token/TokenDropdown.vue';
import Notice from '@/components/Notice.vue';
import FormattedInput from '@/components/FormattedInput.vue';
import InputBalance from '@/components/form/InputBalance.vue';
import {
  calculateMinADAAmountIntoCardanoWhenOnlyBridgeFromCardano,
  normalizeAmountByMinDecimals,
} from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useBalances } from '@/store/modules/tokens/useBalances';
import {
  canUseFarmLP,
  useEasyModeForm,
} from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeForm';
import { useEasyModeAddLiquidityBalanceErrors } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeAddLiquidityBalanceErrors';
import { useMilkomedaWSCBridgeCalculations } from '@/composables/milkomeda-wrapped-smartcontract/useMilkomedaWSCBridgeCalculations';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddLiquidityInputPanel',
  setup(__props) {

const { state } = useStore();
const { t } = useI18n();
const { easyModeForm: commonEasyModeForm, calculateHowManyTokensCanBridgeFromCardano } =
  useEasyModeForm();
const easyModeForm = commonEasyModeForm as EasyModeAddLiquidityForm;
const { getTokenBySymbolAndChainId } = useTokens();
const { balanceByTokenSymbolAndChainId } = useBalances();
const { estimateMaxBridgeAmountFromCardano } = useMilkomedaWSCBridgeCalculations();

const currencyOptions = [USD_TOKEN, easyModeForm.portfolio.baseToken.unwrapWETH()];
const isADABaseToken = currencyOptions[1].symbol === 'ADA';
const milkomedaADA = isADABaseToken ? (currencyOptions[1] as Token) : undefined;

const focus = ref(false);
const inputValue = ref('');
const selectedCurrency = ref(currencyOptions[0].symbol);
const maxBalanceInSelectedCurrency = ref('0');
const isCalculateMaxBalance = ref(false);

const { allBalancesSumInBaseToken, isNotEnoughBalance, isNotEnoughBalanceOfSelectedTokens } =
  useEasyModeAddLiquidityBalanceErrors();

const isUSDOptionSelected = computed(() => selectedCurrency.value === currencyOptions[0].symbol);
const inputInBaseTokenEquivalent = computed(() => {
  if (isUSDOptionSelected.value) {
    return new BigNumber(inputValue.value || 0).div(easyModeForm.portfolio.priceInUSD).toFixed();
  }

  return inputValue.value;
});
const balanceInSelectedCurrency = computed(() => {
  if (isUSDOptionSelected.value) {
    return easyModeForm.portfolio.priceInUSD.multipliedBy(allBalancesSumInBaseToken.value);
  }

  return allBalancesSumInBaseToken.value;
});

const balanceError = computed(() => {
  return isNotEnoughBalance.value;
});
const textPairData = computed(() => {
  const decimals =
    selectedCurrency.value === USD_TOKEN.symbol ? 2 : easyModeForm.portfolio.baseToken.decimals;

  return {
    value: balanceInSelectedCurrency.value,
    tooltip: balanceInSelectedCurrency.value.decimalPlaces(decimals).toFixed(),
    symbol: selectedCurrency.value,
  };
});

// STAKE TO THE FARM
const isEnabledStakeToFarm = computed(() => canUseFarmLP(easyModeForm.portfolio, state.farming.lp));
// NOTE
// set init value for `useFarmsLp` when user see current panel
easyModeForm.useFarmsLp = canUseFarmLP(easyModeForm.portfolio, state.farming.lp);

const onSelectCurrency = ({ symbol }: { symbol: string }) => {
  selectedCurrency.value = symbol;
  easyModeForm.selectedCurrencySymbol = symbol;
};

const isUSDInput = computed(() => {
  return selectedCurrency.value === USD_TOKEN.symbol;
});

const inputDecimals = computed(() =>
  isUSDInput.value ? USD_DECIMAL_PRECISION : easyModeForm.portfolio.baseToken.decimals,
);

async function calculateMaxAmountInADA() {
  // NOTE:
  // allBalancesSumInBaseToken without fees
  const totalBalanceInADA = allBalancesSumInBaseToken.value;
  let maxBalance = totalBalanceInADA;

  // NOTE:
  // When ADA and WSC enabled
  if (ENABLE_FAKE_CARDANO_NETWORK && milkomedaADA) {
    const cardanoADA = getTokenBySymbolAndChainId('ADA', +DEFAULT_CARDANO_CHAIN_ID);

    const balanceTokenIntoCardanoInToken =
      balanceByTokenSymbolAndChainId(
        cardanoADA.symbol!,
        +DEFAULT_CARDANO_CHAIN_ID!,
      ).value?.balance.toExact() ?? '0';

    const estimatedMaxBridgeAmountFromCardano = await estimateMaxBridgeAmountFromCardano(
      cardanoADA,
    );

    // balance in Cardano without fees
    const maxBridgeAmountFromCardanoByBalance = max(
      BigNumber(balanceTokenIntoCardanoInToken).minus(
        calculateMinADAAmountIntoCardanoWhenOnlyBridgeFromCardano(
          calculateHowManyTokensCanBridgeFromCardano(),
        ),
      ),
      0,
    );

    const maxBridgeAmountFromCardano = min(
      estimatedMaxBridgeAmountFromCardano ?? 0,
      maxBridgeAmountFromCardanoByBalance,
    );

    // NOTE:
    // totalBalanceInADA already without fees
    maxBalance = totalBalanceInADA
      .minus(maxBridgeAmountFromCardanoByBalance)
      .plus(maxBridgeAmountFromCardano ?? 0);

    maxBalance = normalizeAmountByMinDecimals(maxBalance.toString(), milkomedaADA);
  }

  return maxBalance;
}

async function calculateMaxAmount() {
  let maxBalance = balanceInSelectedCurrency.value;

  console.log('easyModeForm : ', easyModeForm);

  if (isADABaseToken) {
    maxBalance = await calculateMaxAmountInADA();
  }

  if (isUSDInput.value && isADABaseToken) {
    maxBalance = easyModeForm.portfolio.priceInUSD.multipliedBy(maxBalance);
  }

  return maxBalance.toFixed();
}

async function onSetMax() {
  inputValue.value = maxBalanceInSelectedCurrency.value;
}

easyModeForm.selectedCurrencySymbol = selectedCurrency.value!;

watch(
  balanceInSelectedCurrency,
  async () => {
    isCalculateMaxBalance.value = true;
    maxBalanceInSelectedCurrency.value = await calculateMaxAmount();
    isCalculateMaxBalance.value = false;
  },
  {
    immediate: true,
  },
);

watch(
  inputInBaseTokenEquivalent,
  value => (easyModeForm.baseTokenAmount = new BigNumber(value || 0)),
);

watch(balanceError, value => {
  if (easyModeForm.action === 'addLiquidity') {
    easyModeForm.balanceError = value;
  }
});

watch(isNotEnoughBalanceOfSelectedTokens, value => {
  if (easyModeForm.action === 'addLiquidity') {
    easyModeForm.balanceOfSelectedTokensError = value;
  }
});

watch(
  () => easyModeForm.baseTokenAmount,
  value => {
    if (!value.eq(inputInBaseTokenEquivalent.value) && inputValue.value !== '') {
      if (isUSDOptionSelected.value) {
        inputValue.value = value
          .multipliedBy(easyModeForm.portfolio.priceInUSD)
          .toFixed(USD_DECIMAL_PRECISION, BigNumber.ROUND_DOWN);
        return;
      }

      inputValue.value = value.toFixed();
    }
  },
);

return (_ctx: any,_cache: any) => {
  const _component_Checkbox = _resolveComponent("Checkbox")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      class: _normalizeClass([{
        'panel-focus': focus.value,
        'panel-error': _unref(balanceError),
      }, "p-panel flex flex-column input-panel justify-content-between"])
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(InputBalance, {
          "balance-value": _unref(textPairData).value,
          "balance-symbol": _unref(textPairData).symbol,
          "is-balance-error": _unref(balanceError),
          tooltip: _ctx.$t('portfolio.tooltip.balance'),
          onSetMax: onSetMax,
          "is-max-disabled": !_unref(allBalancesSumInBaseToken).gt(0) || isCalculateMaxBalance.value,
          "not-error-icon": _unref(state).layout.isMobile
        }, null, 8, ["balance-value", "balance-symbol", "is-balance-error", "tooltip", "is-max-disabled", "not-error-icon"])
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TokenDropdown, {
          tokenList: currencyOptions,
          "on-select": onSelectCurrency
        }),
        _createVNode(FormattedInput, {
          class: "border-none p-0 text-right w-full mt-auto inputtext-lg ml-s2",
          modelValue: inputValue.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputValue).value = $event)),
          focus: focus.value,
          "onUpdate:focus": _cache[1] || (_cache[1] = ($event: any) => ((focus).value = $event)),
          decimals: _unref(inputDecimals),
          disabled: isCalculateMaxBalance.value
        }, null, 8, ["modelValue", "focus", "decimals", "disabled"])
      ])
    ], 2),
    (_unref(balanceError))
      ? (_openBlock(), _createBlock(Notice, {
          key: 0,
          text: _unref(t)('portfolio.easyModeTranslations.addLiquidity.balanceError'),
          type: "danger",
          class: "mt-s1"
        }, null, 8, ["text"]))
      : _createCommentVNode("", true),
    (_unref(isEnabledStakeToFarm))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_Checkbox, {
            modelValue: _unref(easyModeForm).useFarmsLp,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(easyModeForm).useFarmsLp) = $event)),
            binary: true,
            "input-id": "stakeToFarm"
          }, null, 8, ["modelValue"]),
          _createElementVNode("label", _hoisted_4, _toDisplayString(_unref(t)('portfolio.easyModeTranslations.addLiquidity.stakeToFarm')), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})